import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnDestroy} from '@angular/core';
import {AudioRecorderService} from "@store/file-upload/audio-recorder.service";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {BehaviorSubject, EMPTY, of, switchMap} from "rxjs";
import {TranslocoModule} from "@ngneat/transloco";
import {ImageUpload} from "../../../../../../models/questions/ImageUpload";
import {AudioUploadService, IAudioUpload} from "@store/file-upload/audio-upload.service";
import {SurveyViewerService} from "../../../services/survey-viewer.service";
import {AbstractControl} from "@angular/forms";
import {IQuestionComponent} from "../../../../../../interfaces/IQuestionComponent";
import {MatTooltipModule} from "@angular/material/tooltip";
import {PipesModule} from "../../../../../../pipes/pipes.module";

@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgIf,
    AsyncPipe,
    TranslocoModule,
    MatTooltipModule,
    NgForOf,
    PipesModule
  ],
  templateUrl: './audio-recorder.component.html',
  styleUrl: './audio-recorder.component.scss'
})
export class AudioRecorderComponent implements IQuestionComponent, AfterViewChecked, OnDestroy {
  errorMessages: Record<string, {fr: string, en: string}> = {
    noRecordingValidator: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    noTextCanBeHeard: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    uploadFailed: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    }
  }

  isRecording = new BehaviorSubject<boolean>(false);
  private isStopping = false; // Flag pour éviter les appels multiples à stop()
  audioBlob?: Blob;
  audioBlobUrl = new BehaviorSubject<string>(undefined);
  @Input() isSmallScreen: boolean;
  @Input() activeLang: string;
  @Input() question: ImageUpload;

  questionFormControl: AbstractControl<any, any>;
  audioFormControl: AbstractControl<any, any>;
  commentsFormControl: AbstractControl<any, any>;

  audioUrl = '';

  hasError = new BehaviorSubject<Array<{fr: string, en: string}>>([]);
  isTouched = new BehaviorSubject<boolean>(false);

  constructor(
    private recorder: AudioRecorderService,
    public audioUploadService: AudioUploadService,
    public surveyViewerService: SurveyViewerService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngAfterViewChecked() {
    this.audioUploadService.uploadedAudio$[this.question.key] ??= new BehaviorSubject<IAudioUpload>(null);
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
    this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName).get(this.question.statementsOrder[0]);
    this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.commentsControlName).get(this.question.statementsOrder[0]);
    this.isTouched.next(this.audioFormControl?.touched || false);
    if(this.audioFormControl?.value){
      this.audioUrl = this.audioFormControl.value;
      this.audioBlobUrl.next(this.audioFormControl.value)
      this.cdr.detectChanges();
    }
    this.audioUploadService.uploadedAudio$[this.question.key]
      .pipe(switchMap((d) => d && (d.url !== this.audioUrl) ? of(d) : EMPTY))
      .subscribe({
        next: (audioData) => {
          // Toujours garder l'audio même si la transcription est invalide
          this.audioUrl = audioData?.url ?? '';
          
          if (!audioData.transcription?.length) {
            // Transcription invalide : garder l'audio mais afficher une erreur
            this.hasError.next([this.errorMessages.noTextCanBeHeard]);
            this.audioFormControl.setValue(this.audioUrl);
            this.commentsFormControl.setValue('');
            this.audioFormControl.setErrors({noTextCanBeHeard: true});
            this.audioFormControl.markAsTouched();
            this.isTouched.next(true);
            this.audioFormControl.markAsDirty();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
            
            // Mettre à jour l'URL de l'audio blob si disponible
            if (this.audioUrl && !this.audioBlobUrl.value) {
              // Si on a l'URL mais pas le blob local, on peut créer une URL depuis l'URL distante
              // ou simplement garder l'URL existante
              this.audioBlobUrl.next(this.audioUrl);
            }
          } else {
            // Transcription valide : tout est OK
            this.audioFormControl.setValue(this.audioUrl);
            this.commentsFormControl.setValue(audioData.transcription);
            this.audioFormControl.setErrors(null);
            this.audioFormControl.markAsTouched();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
            this.hasError.next([]);
            
            // Mettre à jour l'URL de l'audio blob si disponible
            if (this.audioUrl && !this.audioBlobUrl.value) {
              this.audioBlobUrl.next(this.audioUrl);
            }
          }
        },
        error: () => {
          this.audioFormControl.setValue(null);
          this.audioFormControl.setErrors({uploadFailed: true});
          this.audioFormControl.markAsTouched();
          this.isTouched.next(true);
          this.audioFormControl.markAsDirty();
          this.audioFormControl.updateValueAndValidity({emitEvent: true});
          this.hasError.next([this.errorMessages.uploadFailed]);
        }
      });
    this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName)
      .get(this.question.statementsOrder[0]).statusChanges.subscribe(value => {
      if (value === 'VALID') {
        this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).markAsTouched()
        this.hasError.next([]);
      } else {
        // Only show errors if the control is touched or all form controls are marked as touched
        if (this.audioFormControl.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
          this.updateErrorMessages();
        }
      }
    })
    this.audioFormControl.statusChanges.subscribe(() => {
      this.isTouched.next(this.audioFormControl?.touched || false);
    });
    this.surveyViewerService.allFormControlsMarkedAsTouched$.subscribe(() => {
      this.updateErrorMessages();
    });
    this.surveyViewerService.activeSurvey$.subscribe(() => {this.updateErrorMessages()});
  }

  private updateErrorMessages() {
    // Only show errors if the control is touched or all form controls are marked as touched
    if (this.audioFormControl?.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
      if (this.audioFormControl?.errors) {
        const errors = Object.entries(this.audioFormControl.errors).map(([key, val]) => {
          if (val) {
            return this.errorMessages[key] || this.errorMessages.noRecordingValidator;
          }
        }).filter((msg): msg is {fr: string, en: string} => Boolean(msg));
        this.hasError.next(errors);
      } else if (this.audioFormControl?.valid) {
        this.hasError.next([]);
      }
    } else {
      // Don't show errors if the control hasn't been touched yet
      this.hasError.next([]);
    }
  }

  async start() {
    // Réinitialiser le flag d'arrêt
    this.isStopping = false;
    this.isRecording.next(true);
    await this.recorder.startRecording(this.question.key);
    this.audioFormControl.markAsUntouched();
    this.isTouched.next(false);
  }

  async stop() {
    // Vérifier qu'un enregistrement est vraiment en cours et qu'on n'est pas déjà en train d'arrêter
    if (!this.isRecording.value || this.isStopping) {
      return;
    }

    this.isStopping = true;
    this.isRecording.next(false);
    
    try {
      this.audioBlob = await this.recorder.stopRecording(this.question.key);
      if (this.audioBlob) {
        this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
        if (this.commentsFormControl) {
          this.commentsFormControl.setValue('Invalid');
        }
        this.upload();
      }
    } catch (error) {
      // Si l'enregistrement n'existe pas ou a déjà été arrêté, on nettoie juste l'état
      console.warn('Error stopping recording:', error);
      // Réinitialiser l'état si nécessaire
      if (!this.audioBlob) {
        this.audioBlob = undefined;
        this.audioBlobUrl.next(undefined);
      }
    } finally {
      this.isStopping = false;
    }
  }

  eraseAudio() {
    // Annuler l'upload en cours s'il y en a un pour éviter le chevauchement
    if (this.audioUploadService.isUploading$[this.question.key]?.value === true) {
      this.audioUploadService.cancelUpload(this.question.key);
    }
    
    this.audioBlob = undefined;
    this.audioBlobUrl.next(undefined);
    this.audioUploadService.uploadedAudio$[this.question.key]?.next(null);
    this.audioUploadService.isUploading$[this.question.key]?.next(false);
    this.audioFormControl.setValue(null);
    this.audioFormControl.setErrors({noRecordingValidator: true});
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.commentsFormControl.setValue('');
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    this.updateErrorMessages();
  }

  upload() {
    if (!this.audioBlob) return;
    
    // Initialiser les contrôles s'ils ne le sont pas encore
    if (!this.audioFormControl || !this.questionFormControl) {
      this.initializeFormControls();
    }
    
    if (!this.audioFormControl || !this.questionFormControl) {
      console.error('Form controls not available for upload');
      return;
    }
    
    this.hasError.next([]);
    const provisionalValue = this.audioBlobUrl.value || 'uploading';
    this.audioFormControl.setValue(provisionalValue);
    this.audioFormControl.markAsDirty();
    this.audioFormControl.setErrors(null);
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.questionFormControl.markAsTouched();
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    this.audioUploadService.uploadAudioFile(this.audioBlob, this.question.key, this.surveyViewerService.activeSurvey$.value.id);
  }

  private initializeFormControls() {
    try {
      if (this.surveyViewerService.activeSurvey$.value?.form) {
        this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
        if (this.questionFormControl) {
          this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0])?.get(this.question.audioControlName)?.get(this.question.statementsOrder[0]);
          this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0])?.get(this.question.commentsControlName)?.get(this.question.statementsOrder[0]);
        }
      }
    } catch (error) {
      console.error('Error initializing form controls:', error);
    }
  }

  async ngOnDestroy() {
    // Si un enregistrement est en cours lors de la destruction du composant (changement de page),
    // arrêter l'enregistrement et démarrer la validation automatiquement
    if (this.isRecording.value) {
      try {
        // Initialiser les contrôles s'ils ne le sont pas encore
        if (!this.audioFormControl || !this.questionFormControl) {
          this.initializeFormControls();
        }
        
        // Arrêter l'enregistrement et démarrer la validation
        this.isRecording.next(false);
        this.audioBlob = await this.recorder.stopRecording(this.question.key);
        if (this.audioBlob) {
          this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
          if (this.commentsFormControl) {
            this.commentsFormControl.setValue('Invalid');
          }
          this.upload();
        }
      } catch (error) {
        // Si l'arrêt échoue, on nettoie quand même l'état
        console.error('Error stopping recording on destroy:', error);
        this.isRecording.next(false);
        // Réinitialiser l'état en cas d'erreur
        if (this.audioFormControl) {
          this.audioFormControl.setErrors({noRecordingValidator: true});
          this.audioFormControl.markAsTouched();
        }
      }
    }
  }

}
